import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaWhatsapp, FaTimes, FaGift, FaDownload } from 'react-icons/fa';

const WhatsAppPopup = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    // Check if device is mobile
    const checkMobile = () => {
      const isMobileDevice = window.innerWidth <= 768;
      setIsMobile(isMobileDevice);
      
      // Show popup only on mobile and if not dismissed or dismissal has expired
      if (isMobileDevice) {
        const dismissedTime = localStorage.getItem('whatsappPopupDismissed');
        const currentTime = new Date().getTime();
        
        // Show popup if never dismissed OR if dismissal time has expired
        if (!dismissedTime || currentTime > parseInt(dismissedTime)) {
          // Clear expired dismissal
          if (dismissedTime && currentTime > parseInt(dismissedTime)) {
            localStorage.removeItem('whatsappPopupDismissed');
          }
          
          // Show popup after 2 seconds delay
          setTimeout(() => {
            setIsVisible(true);
          }, 2000);
        }
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleClose = () => {
    setIsVisible(false);
    // Remember that user dismissed popup (expires after 24 hours)
    const expiryTime = new Date().getTime() + (86400000); // 24 hours in milliseconds
    localStorage.setItem('whatsappPopupDismissed', expiryTime.toString());
  };

  const handleWhatsAppClick = () => {
    const phoneNumber = '918056064224'; // Your WhatsApp number
    const message = 'Hi, I am interested! Can you please share me more details?';
    const whatsappURL = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    
    window.open(whatsappURL, '_blank');
    handleClose();
  };

  // Don't render on desktop
  if (!isMobile) return null;

  return (
    <AnimatePresence>
      {isVisible && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9998]"
            onClick={handleClose}
          />
          
          {/* Popup */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8, y: 50 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 50 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="fixed top-[25%] left-[5%] transform -translate-x-1/2 -translate-y-1/2 z-[9999] w-[90vw] max-w-sm"
          >
            <div className="relative bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl shadow-2xl border border-green-200 overflow-hidden">
              {/* Close Button */}
              <button
                onClick={handleClose}
                className="absolute top-3 right-3 w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors duration-200 z-10"
              >
                <FaTimes className="text-gray-600 text-sm" />
              </button>

              {/* Header with Animation */}
              <div className="bg-gradient-to-r from-green-500 to-emerald-500 px-4 py-3 text-white relative overflow-hidden">
                <motion.div
                  animate={{ 
                    backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"]
                  }}
                  transition={{ duration: 3, repeat: Infinity }}
                  className="absolute inset-0 bg-gradient-to-r from-green-400 via-emerald-400 to-green-400 opacity-30"
                  style={{ backgroundSize: "200% 200%" }}
                />
                <div className="relative flex items-center justify-center gap-2">
                  <motion.div
                    animate={{ rotate: [0, 10, -10, 0] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    <FaGift className="text-xl" />
                  </motion.div>
                  <span className="font-bold text-sm">Special Offer!</span>
                </div>
              </div>

              {/* Content */}
              <div className="p-5 text-center">
                {/* WhatsApp Icon with Animation */}
                <motion.div
                  animate={{ 
                    scale: [1, 1.1, 1],
                    rotate: [0, 5, -5, 0]
                  }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full mb-4 shadow-lg"
                >
                  <FaWhatsapp className="text-white text-2xl" />
                </motion.div>

                {/* Main Message */}
                <h3 className="text-lg font-bold text-gray-800 mb-2">
                  Invest in your Website Today! 📋
                </h3>
                
                <p className="text-sm text-gray-600 mb-4 leading-relaxed">
                  Connect with us on WhatsApp and get instant access to our 
                  <span className="font-semibold text-green-600"> complete service brochure </span> with pricing details!
                </p>

                {/* Features */}
                <div className="bg-white/70 rounded-lg p-3 mb-4 text-left">
                  <div className="flex items-center gap-2 text-xs text-gray-700 mb-1">
                    <span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>
                    Instant response guaranteed
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-700 mb-1">
                    <span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>
                    Free consultation & quotes
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-700">
                    <span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>
                    Portfolio examples included
                  </div>
                </div>

                {/* CTA Button */}
                <motion.button
                  onClick={handleWhatsAppClick}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="w-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white font-bold py-3 px-4 rounded-xl shadow-lg transition-all duration-300 flex items-center justify-center gap-2"
                >
                  <FaWhatsapp className="text-lg" />
                  <span>Chat on WhatsApp</span>
                  <FaDownload className="text-sm" />
                </motion.button>

                {/* Small Text */}
                <p className="text-xs text-gray-500 mt-3">
                  Just say "Hi" and we'll send you everything! 🚀
                </p>
              </div>

              {/* Decorative Elements */}
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-green-400 via-emerald-400 to-green-400"></div>
              
              {/* Floating Particles */}
              {[...Array(3)].map((_, i) => (
                <motion.div
                  key={i}
                  className="absolute w-2 h-2 bg-green-300 rounded-full opacity-30"
                  style={{
                    top: `${20 + i * 25}%`,
                    right: `${10 + i * 15}%`,
                  }}
                  animate={{
                    y: [-10, 10, -10],
                    opacity: [0.3, 0.7, 0.3],
                  }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    delay: i * 0.5,
                  }}
                />
              ))}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default WhatsAppPopup;
